import {
  type BombeoDetails,
  type Muelle,
  MUELLES,
  type PlaceEvent,
  type Scale,
  type ScaleEvent,
  ScaleEventType,
  type SpecialEvent,
  type StayOperation,
} from './types'

export const isMuelle = (value: string): value is Muelle => MUELLES.some(muelle => muelle === value)

const isSpecialEvent = (event: ScaleEvent): event is SpecialEvent => event.type !== ScaleEventType.PLACE

const byStartDesc = (a: ScaleEvent, b: ScaleEvent): number => {
  if (a.startTime === b.startTime) return a.id < b.id ? 1 : a.id > b.id ? -1 : 0
  return Date.parse(b.startTime) - Date.parse(a.startTime)
}

export const sortEventsDesc = <T extends ScaleEvent>(events: readonly T[]): T[] => [...events].sort(byStartDesc)

export const currentMuelle = (events: readonly ScaleEvent[], now: number): Muelle | null => {
  const started = sortEventsDesc(events.filter((e): e is PlaceEvent => e.type === ScaleEventType.PLACE && Date.parse(e.startTime) <= now))
  const inProgress = started.find(e => e.endTime === null || Date.parse(e.endTime) >= now)
  return (inProgress ?? started[0])?.muelle ?? null
}

export const isWithinStay = (stay: Pick<PlaceEvent, 'startTime' | 'endTime'>, at: number): boolean =>
  Date.parse(stay.startTime) <= at && (stay.endTime === null || at < Date.parse(stay.endTime))

const staysOf = (events: readonly ScaleEvent[]): PlaceEvent[] => events.filter((e): e is PlaceEvent => e.type === ScaleEventType.PLACE)

export const isWithinAnyStay = (events: readonly ScaleEvent[], at: number): boolean => staysOf(events).some(stay => isWithinStay(stay, at))

export const specialEventDefaultStart = (events: readonly ScaleEvent[], now: number): number | null => {
  if (isWithinAnyStay(events, now)) return now
  const upcoming = staysOf(events)
    .map(stay => Date.parse(stay.startTime))
    .filter(start => start > now)
  return upcoming.length > 0 ? Math.min(...upcoming) : null
}

export type TimeRangeError = 'INVALID_START' | 'INVALID_END' | 'END_BEFORE_START'

export const validateTimeRange = (startTime: string, endTime: string | null): TimeRangeError | null => {
  const start = Date.parse(startTime)
  if (Number.isNaN(start)) return 'INVALID_START'
  if (endTime === null) return null
  const end = Date.parse(endTime)
  if (Number.isNaN(end)) return 'INVALID_END'
  return end < start ? 'END_BEFORE_START' : null
}

export interface ScaleWithEvents {
  scale: Scale
  events: ScaleEvent[]
}

export const mergeManualScale = (manual: ScaleWithEvents, source: ScaleWithEvents): ScaleWithEvents => {
  if (manual.scale.id !== source.scale.id) throw new Error(`Cannot merge scale ${manual.scale.id} into ${source.scale.id}`)
  const sourceIds = new Set(source.events.map(event => event.id))
  const keptSpecials = manual.events.filter(isSpecialEvent).filter(event => !sourceIds.has(event.id))
  return {
    scale: { ...source.scale, isManual: true, seenInSource: true },
    events: [...source.events, ...keptSpecials],
  }
}

const agreed = <T>(values: readonly (T | null)[]): T | null => {
  const [first] = values
  return first !== undefined && first !== null && values.every(value => value === first) ? first : null
}

export const bombeoPrefill = (operations: readonly StayOperation[]): Omit<BombeoDetails, 'connectionPoint'> => {
  const live = operations.filter(operation => operation.state !== 'Anulado')
  const operationKind = agreed(live.map(operation => operation.kind))
  const productClass = agreed(live.map(operation => operation.productClass))
  const quantities = live.map(operation => operation.quantityT)
  const quantityT =
    live.length > 0 && operationKind !== null && productClass !== null && quantities.every(quantity => quantity !== null)
      ? Math.round(quantities.reduce<number>((sum, quantity) => sum + quantity, 0) * 1000) / 1000
      : null
  return { operationKind, productClass, product: agreed(live.map(operation => operation.goods)), quantityT }
}

const HOUR_MS = 3_600_000

export interface BombeoMetrics {
  inicio: number | null
  rendimiento: number | null
  salida: number | null
}

const BOMBEO_THRESHOLDS = { maxInicio: 8, minRendimiento: 20, maxSalida: 6 } as const

export type BombeoCompletion = 'COMPLETE' | 'INCOMPLETE' | 'UNKNOWN'

interface Span {
  start: number
  end: number | null
}
type EndedSpan = Span & { end: number }

const spansOf = (events: readonly ScaleEvent[], type: ScaleEventType): Span[] =>
  events
    .filter(event => event.type === type)
    .map(event => ({ start: Date.parse(event.startTime), end: event.endTime === null ? null : Date.parse(event.endTime) }))

const endedBombeos = (events: readonly ScaleEvent[]): EndedSpan[] | null => {
  const bombeos = spansOf(events, ScaleEventType.BOMBEO)
  const ended = bombeos.filter((span): span is EndedSpan => span.end !== null)
  return bombeos.length > 0 && ended.length === bombeos.length ? ended : null
}

const earliestStart = (spans: readonly Span[]): number => Math.min(...spans.map(span => span.start))
const latestEnd = (spans: readonly Pick<EndedSpan, 'end'>[]): number => Math.max(...spans.map(span => span.end))

export const bombeoInicio = (events: readonly ScaleEvent[]): number | null => {
  const bombeos = endedBombeos(events)
  const places = spansOf(events, ScaleEventType.PLACE)
  if (bombeos === null || places.length === 0) return null
  return (earliestStart(bombeos) - earliestStart(places)) / HOUR_MS
}

export const bombeoRendimiento = (events: readonly ScaleEvent[]): number | null => {
  const bombeos = endedBombeos(events)
  if (bombeos === null || spansOf(events, ScaleEventType.PLACE).length === 0) return null
  const span = latestEnd(bombeos) - earliestStart(bombeos)
  if (span <= 0) return null
  const pumped = bombeos.reduce((sum, bombeo) => sum + (bombeo.end - bombeo.start), 0)
  return (pumped * 24) / span
}

export const bombeoSalida = (events: readonly ScaleEvent[]): number | null => {
  const bombeos = endedBombeos(events)
  const places = sortEventsDesc(events.filter((event): event is PlaceEvent => event.type === ScaleEventType.PLACE))
  const lastStayEnd = places[0]?.endTime
  if (bombeos === null || lastStayEnd === undefined || lastStayEnd === null) return null
  const ends = places.flatMap(place => (place.endTime === null ? [] : [{ end: Date.parse(place.endTime) }]))
  return (latestEnd(ends) - latestEnd(bombeos)) / HOUR_MS
}

export const bombeoMetrics = (events: readonly ScaleEvent[]): BombeoMetrics => ({
  inicio: bombeoInicio(events),
  rendimiento: bombeoRendimiento(events),
  salida: bombeoSalida(events),
})

export const bombeoCompletion = ({ inicio, rendimiento, salida }: BombeoMetrics): BombeoCompletion => {
  if (inicio === null || rendimiento === null || salida === null) return 'UNKNOWN'
  const { maxInicio, minRendimiento, maxSalida } = BOMBEO_THRESHOLDS
  return inicio <= maxInicio && rendimiento >= minRendimiento && salida <= maxSalida ? 'COMPLETE' : 'INCOMPLETE'
}
